import React, { useState, useEffect, useMemo } from 'react';
import { 
  Folder, File, ServerCrash, RefreshCw, Loader2, HardDrive, 
  FileArchive, Image as ImageIcon, Video, FileText, Search, 
  LayoutGrid, List, Copy, Check, FileCode, ChevronRight, Power
} from 'lucide-react';

import SharedSSHUnlock from './SharedSSHUnlock';

const UploadsTab = ({ tenant, sshSessionId, socket, isVisible, sshStatus, sshError, onConnect, onDisconnect, isLeftPanelOpen, onToggleLeftPanel }) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentPath, setCurrentPath] = useState(''); // relative subpath (e.g., 'branding' or 'chat/images')
    
    // UI Enhancements
    const [searchFilter, setSearchFilter] = useState('');
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
    const [copiedName, setCopiedName] = useState(null);

    useEffect(() => {
        if (isVisible && sshSessionId) {
            fetchFiles(currentPath);
        }
    }, [isVisible, sshSessionId, currentPath]);

    useEffect(() => {
        if (!socket) return;
        
        const handleResult = (data) => {
            setLoading(false);
            if (data.success) {
                setFiles(data.files || []);
                setError('');
            } else {
                setError(data.error);
                setFiles([]);
            }
        };

        socket.on('ssh:filesResult', handleResult);
        return () => {
            socket.off('ssh:filesResult', handleResult);
        };
    }, [socket]);

    const fetchFiles = (path = currentPath) => {
        if (!sshSessionId || !socket) {
            setError("Veuillez d'abord vous connecter au terminal SSH (onglet SSH).");
            return;
        }
        setLoading(true);
        setError('');
        socket.emit('ssh:listFiles', { sessionId: sshSessionId, dbName: tenant.DbName, subPath: path });
    };

    const navigateToDirectory = (folderName) => {
        const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
        setCurrentPath(newPath);
        setSearchFilter('');
    };

    const navigateUp = () => {
        if (!currentPath) return;
        const parts = currentPath.split('/');
        parts.pop();
        setCurrentPath(parts.join('/'));
        setSearchFilter('');
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopiedName(text);
        setTimeout(() => setCopiedName(null), 2000);
    };

    // Filter files by search
    const filteredFiles = useMemo(() => {
        if (!searchFilter.trim()) return files;
        const term = searchFilter.toLowerCase().trim();
        return files.filter(f => f.name.toLowerCase().includes(term));
    }, [files, searchFilter]);

    // Format raw byte sizes into human readable format (KB, MB)
    const formatSize = (file) => {
        if (file.isDirectory || file.permissions?.startsWith('d')) return '—';
        const sizeStr = file.size;
        if (!sizeStr) return '0 B';
        if (/[a-zA-Z]/.test(sizeStr) && !sizeStr.endsWith('B')) return sizeStr;
        const bytes = parseInt(sizeStr, 10);
        if (isNaN(bytes)) return sizeStr;
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    // Calculate total directory size
    const totalStorageFormatted = useMemo(() => {
        let total = 0;
        files.forEach(f => {
            if (!f.isDirectory && !f.permissions?.startsWith('d')) {
                const bytes = parseInt(f.size, 10);
                if (!isNaN(bytes)) total += bytes;
            }
        });
        return formatSize({ size: total });
    }, [files]);

    const getFileMeta = (file) => {
        const isDir = file.isDirectory || file.permissions?.startsWith('d');
        if (isDir) {
            return {
                icon: <Folder size={20} className="text-amber-500 fill-amber-100 shrink-0" />,
                badgeBg: 'bg-amber-100 text-amber-800 border-amber-300 font-black',
                ext: 'DOSSIER',
                isDir: true
            };
        }

        const ext = file.name.split('.').pop().toLowerCase();
        if (['jpg', 'jpeg', 'png', 'svg', 'gif', 'webp'].includes(ext)) {
            return {
                icon: <ImageIcon size={19} className="text-pink-500 shrink-0" />,
                badgeBg: 'bg-pink-50 text-pink-700 border-pink-200 font-bold',
                ext: ext.toUpperCase(),
                isDir: false
            };
        }
        if (['mp4', 'webm', 'mov', 'avi'].includes(ext)) {
            return {
                icon: <Video size={19} className="text-violet-500 shrink-0" />,
                badgeBg: 'bg-violet-50 text-violet-700 border-violet-200 font-bold',
                ext: ext.toUpperCase(),
                isDir: false
            };
        }
        if (['pdf'].includes(ext)) {
            return {
                icon: <FileText size={19} className="text-rose-500 shrink-0" />,
                badgeBg: 'bg-rose-50 text-rose-700 border-rose-200 font-bold',
                ext: ext.toUpperCase(),
                isDir: false
            };
        }
        if (['zip', 'gz', 'tar', 'rar', '7z'].includes(ext)) {
            return {
                icon: <FileArchive size={19} className="text-amber-500 shrink-0" />,
                badgeBg: 'bg-amber-50 text-amber-700 border-amber-200 font-bold',
                ext: ext.toUpperCase(),
                isDir: false
            };
        }
        if (['js', 'json', 'html', 'css', 'ts', 'php'].includes(ext)) {
            return {
                icon: <FileCode size={19} className="text-sky-500 shrink-0" />,
                badgeBg: 'bg-sky-50 text-sky-700 border-sky-200 font-bold',
                ext: ext.toUpperCase(),
                isDir: false
            };
        }
        return {
            icon: <File size={19} className="text-slate-400 shrink-0" />,
            badgeBg: 'bg-slate-100 text-slate-600 border-slate-200 font-bold',
            ext: ext.toUpperCase(),
            isDir: false
        };
    };

    return (
        <div className="flex-1 flex flex-col h-full bg-slate-100 overflow-hidden">
            {/* Header Bar */}
            <div className="h-16 px-4 lg:px-6 border-b border-slate-200 flex items-center justify-between bg-white shrink-0 z-10 shadow-xs">
                <div className="flex items-center gap-3">
                    {!isLeftPanelOpen && (
                        <button
                            onClick={onToggleLeftPanel}
                            className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg border border-slate-200 transition-colors shrink-0"
                            title="Déplier Fiche Instance"
                        >
                            <ChevronRight size={18} />
                        </button>
                    )}
                    <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg border border-blue-100">
                        <Folder size={20} />
                    </div>
                    <div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <h3 className="text-base font-bold text-slate-800 tracking-tight">Explorateur d'Uploads</h3>
                            <button 
                                onClick={() => setCurrentPath('')}
                                className="text-xs font-mono font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100 transition-colors"
                            >
                                /opt/xora/uploads/{tenant?.DbName}
                            </button>
                            {currentPath && currentPath.split('/').map((part, idx, arr) => (
                                <React.Fragment key={idx}>
                                    <span className="text-slate-300 text-xs font-bold">/</span>
                                    <button
                                        onClick={() => setCurrentPath(arr.slice(0, idx + 1).join('/'))}
                                        className="text-xs font-mono font-black px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200 transition-colors"
                                    >
                                        {part}
                                    </button>
                                </React.Fragment>
                            ))}
                        </div>
                        <div className="text-xs text-slate-600 font-medium flex items-center gap-2 mt-0.5">
                            {sshStatus === 'connected' && (
                                <>
                                    <span>{files.length} élément{files.length > 1 ? 's' : ''}</span>
                                    <span>•</span>
                                    <span className="font-bold text-slate-800">Taille fichiers : {totalStorageFormatted}</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {sshStatus === 'connected' && (
                        <>
                            <button
                                onClick={() => fetchFiles()}
                                disabled={loading}
                                className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-slate-700 hover:text-blue-700 bg-slate-50 hover:bg-blue-50 border border-slate-200 rounded-lg transition-all"
                                title="Rafraîchir le dossier"
                            >
                                <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
                                <span className="hidden sm:inline">Actualiser</span>
                            </button>
                            <button
                                onClick={() => {
                                    if (onDisconnect) {
                                        onDisconnect();
                                    } else if (tenant?.id) {
                                        sessionStorage.removeItem(`ssh_pass_${tenant.id}`);
                                        if (socket && sshSessionId) socket.emit('ssh:disconnect', { sessionId: sshSessionId });
                                    }
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg transition-all shadow-xs"
                                title="Déconnecter le SSH"
                            >
                                <Power size={14} />
                                <span>Déconnecter</span>
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Content Viewport */}
            <div className="flex-1 flex flex-col overflow-hidden relative">
                {sshStatus !== 'connected' || (error && error.includes('Non connecté')) ? (
                    <SharedSSHUnlock 
                        tenant={tenant} 
                        status={sshStatus === 'connected' ? 'idle' : sshStatus} 
                        errorMessage={sshError || error} 
                        onConnect={onConnect} 
                        title="Accès Sécurisé aux Fichiers"
                        description="Connectez-vous via SSH au VPS pour explorer et gérer les fichiers média uploadés."
                    />
                ) : (
                    <div className="flex-1 flex flex-col min-w-0 bg-white overflow-hidden">
                        {/* Toolbar: Search, Parent Dir & View Mode */}
                        <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 bg-slate-50/80 shrink-0 gap-3">
                            <div className="flex items-center gap-2 flex-1 max-w-lg">
                                {/* Back to Parent Directory Button */}
                                {currentPath && (
                                    <button
                                        onClick={navigateUp}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 rounded-lg transition-all shadow-xs shrink-0"
                                        title="Dossier parent"
                                    >
                                        <span>← Dossier parent</span>
                                    </button>
                                )}

                                {/* Search Filter */}
                                <div className="relative flex-1">
                                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Rechercher fichier / dossier..."
                                        value={searchFilter}
                                        onChange={(e) => setSearchFilter(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 text-xs font-medium bg-white border border-slate-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-400 transition-all shadow-xs"
                                    />
                                </div>
                            </div>

                            {/* View Switcher */}
                            <div className="flex items-center gap-2 shrink-0">
                                <div className="flex bg-slate-200/80 p-0.5 rounded-lg border border-slate-200">
                                    <button 
                                        onClick={() => setViewMode('list')}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                                            viewMode === 'list' ? 'bg-white text-blue-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                                        }`}
                                    >
                                        <List size={14} /> Liste
                                    </button>
                                    <button 
                                        onClick={() => setViewMode('grid')}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                                            viewMode === 'grid' ? 'bg-white text-blue-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                                        }`}
                                    >
                                        <LayoutGrid size={14} /> Grille
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Main File List Container */}
                        <div className="flex-1 overflow-auto p-4 lg:p-6 bg-slate-100/60">
                            {error ? (
                                <div className="max-w-xl mx-auto my-12 text-center p-8 bg-white border border-rose-200 rounded-2xl shadow-xs">
                                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-rose-50 text-rose-600 mb-3">
                                        <ServerCrash size={28} />
                                    </div>
                                    <h3 className="text-base font-bold text-slate-800">Impossible d'accéder aux fichiers</h3>
                                    <p className="text-sm text-slate-500 mt-1 mb-4">{error}</p>
                                    <button
                                        onClick={() => {
                                            setError('');
                                            fetchFiles();
                                        }}
                                        className="px-4 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                                    >
                                        Réessayer la connexion
                                    </button>
                                </div>
                            ) : loading ? (
                                <div className="flex flex-col items-center justify-center h-full p-16">
                                    <Loader2 size={36} className="animate-spin text-blue-600 mb-3" />
                                    <p className="text-sm font-bold text-slate-700">Chargement du répertoire...</p>
                                    <p className="text-xs text-slate-400 mt-0.5">Récupération du contenu</p>
                                </div>
                            ) : filteredFiles.length === 0 ? (
                                <div className="max-w-md mx-auto my-12 text-center border-2 border-dashed border-slate-200 rounded-2xl p-10 bg-white">
                                    <HardDrive size={40} className="mx-auto text-slate-300 mb-3" />
                                    <h3 className="text-sm font-bold text-slate-800">
                                        {searchFilter ? 'Aucun élément ne correspond' : 'Dossier vide'}
                                    </h3>
                                    <p className="text-xs text-slate-400 mt-1">
                                        {searchFilter ? 'Essayez avec un autre terme de recherche.' : 'Ce répertoire ne contient aucun fichier ou dossier.'}
                                    </p>
                                    {currentPath && (
                                        <button
                                            onClick={navigateUp}
                                            className="mt-4 px-3.5 py-2 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                                        >
                                            ← Remonter d'un dossier
                                        </button>
                                    )}
                                </div>
                            ) : viewMode === 'list' ? (
                                /* List View Table */
                                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs">
                                    <table className="w-full text-left border-collapse whitespace-nowrap">
                                        <thead>
                                            <tr className="bg-slate-50 border-b border-slate-200">
                                                <th className="py-3 px-4 text-xs font-black uppercase tracking-wider text-slate-600">Nom</th>
                                                <th className="py-3 px-4 text-xs font-black uppercase tracking-wider text-slate-600">Taille</th>
                                                <th className="py-3 px-4 text-xs font-black uppercase tracking-wider text-slate-600 hidden sm:table-cell">Modifié le</th>
                                                <th className="py-3 px-4 text-xs font-black uppercase tracking-wider text-slate-600 hidden md:table-cell">Permissions</th>
                                                <th className="py-3 px-4 text-xs font-black uppercase tracking-wider text-slate-600 text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 text-xs">
                                            {filteredFiles.map((file, i) => {
                                                const meta = getFileMeta(file);
                                                return (
                                                    <tr 
                                                        key={i} 
                                                        onClick={() => meta.isDir && navigateToDirectory(file.name)}
                                                        className={`transition-colors group ${
                                                            meta.isDir 
                                                                ? 'bg-amber-50/30 hover:bg-amber-100/60 cursor-pointer font-bold' 
                                                                : 'hover:bg-blue-50/40'
                                                        }`}
                                                    >
                                                        <td className="py-3.5 px-4">
                                                            <div className="flex items-center gap-3">
                                                                {meta.icon}
                                                                <span className={`text-xs sm:text-sm max-w-xs sm:max-w-md truncate ${
                                                                    meta.isDir ? 'text-amber-900 font-extrabold group-hover:underline' : 'font-bold text-slate-800'
                                                                }`} title={file.name}>
                                                                    {file.name}
                                                                </span>
                                                                <span className={`text-[10px] px-2 py-0.5 rounded border uppercase font-mono ${meta.badgeBg}`}>
                                                                    {meta.ext}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="py-3.5 px-4 font-mono font-bold text-slate-700 text-xs">
                                                            {formatSize(file)}
                                                        </td>
                                                        <td className="py-3.5 px-4 text-slate-600 font-semibold text-xs hidden sm:table-cell">
                                                            {file.date}
                                                        </td>
                                                        <td className="py-3.5 px-4 font-mono text-xs text-slate-400 hidden md:table-cell">
                                                            {file.permissions}
                                                        </td>
                                                        <td className="py-3.5 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                                                            {meta.isDir ? (
                                                                <button
                                                                    onClick={() => navigateToDirectory(file.name)}
                                                                    className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-800 bg-amber-100 hover:bg-amber-200 px-3 py-1 rounded-md transition-colors"
                                                                >
                                                                    <span>Ouvrir dossier →</span>
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    onClick={() => copyToClipboard(file.name)}
                                                                    className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-blue-600 bg-slate-100 hover:bg-blue-50 px-2.5 py-1 rounded-md transition-colors"
                                                                    title="Copier le nom du fichier"
                                                                >
                                                                    {copiedName === file.name ? (
                                                                        <>
                                                                            <Check size={13} className="text-emerald-500" />
                                                                            <span className="text-emerald-600 font-bold">Copié</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <Copy size={13} />
                                                                            <span>Copier nom</span>
                                                                        </>
                                                                    )}
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                /* Grid View Cards */
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {filteredFiles.map((file, i) => {
                                        const meta = getFileMeta(file);
                                        return (
                                            <div 
                                                key={i} 
                                                onClick={() => meta.isDir && navigateToDirectory(file.name)}
                                                className={`bg-white border rounded-xl p-4 flex flex-col justify-between transition-all group ${
                                                    meta.isDir 
                                                        ? 'border-amber-200 bg-amber-50/20 hover:bg-amber-100/40 hover:border-amber-400 hover:shadow-md cursor-pointer' 
                                                        : 'border-slate-200 hover:shadow-md hover:border-blue-300'
                                                }`}
                                            >
                                                <div>
                                                    <div className="flex items-start justify-between gap-2 mb-3">
                                                        <div className={`p-2.5 rounded-lg border transition-colors ${
                                                            meta.isDir ? 'bg-amber-100/80 border-amber-200' : 'bg-slate-50 border-slate-100 group-hover:bg-blue-50'
                                                        }`}>
                                                            {meta.icon}
                                                        </div>
                                                        <span className={`text-[10px] px-2 py-0.5 rounded border uppercase font-mono ${meta.badgeBg}`}>
                                                            {meta.ext}
                                                        </span>
                                                    </div>

                                                    <h4 className={`font-bold text-xs sm:text-sm truncate mb-1 ${
                                                        meta.isDir ? 'text-amber-900 group-hover:underline' : 'text-slate-800'
                                                    }`} title={file.name}>
                                                        {file.name}
                                                    </h4>
                                                    
                                                    <div className="text-xs font-mono font-bold text-slate-600 mb-2">
                                                        {formatSize(file)}
                                                    </div>
                                                </div>

                                                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 font-medium" onClick={(e) => e.stopPropagation()}>
                                                    <span>{file.date.split(' ')[0]}</span>
                                                    {meta.isDir ? (
                                                        <button
                                                            onClick={() => navigateToDirectory(file.name)}
                                                            className="flex items-center gap-1 text-amber-800 hover:text-amber-950 font-bold text-xs bg-amber-100 px-2 py-1 rounded hover:bg-amber-200 transition-colors"
                                                        >
                                                            <span>Ouvrir →</span>
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => copyToClipboard(file.name)}
                                                            className="flex items-center gap-1 text-slate-600 hover:text-blue-600 bg-slate-50 hover:bg-blue-50 px-2 py-1 rounded transition-colors font-bold text-xs"
                                                            title="Copier le nom"
                                                        >
                                                            {copiedName === file.name ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
                                                            <span>{copiedName === file.name ? 'Copié' : 'Copier'}</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UploadsTab;
